import { NavigationProp, useNavigation } from '@react-navigation/native';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Edit2 } from 'react-native-feather';
import { Body, HoverButton } from 'src/components';
import AnimalIcon from 'src/components/Icons/Animal';
import { IconSize, IS_WEB, Margin, Size } from 'src/constants';
import { Patient } from 'src/interfaces';
import { useAppTheme } from 'src/providers/AppThemeProvider';
import MainStackParamsList from 'src/routes/stacks/MainStack/ParamsList';
import { Navigators } from 'src/routes/stacks/navigators';
import { Screens } from 'src/routes/stacks/screens';
import { GuideElement, STEPS, useTourGuide } from 'src/routes/stacks/PatientStackNavigator/tour';
import { Weight } from 'src/theme';
import styled from 'styled-components/native';

interface Props {
  patient: Patient;
  showEditButton?: boolean;
}

const Header: React.FC<Props> = ({ patient, showEditButton }) => {
  const { t } = useTranslation('patientProfile');
  const { colors } = useAppTheme();
  const { navigate } = useNavigation<NavigationProp<MainStackParamsList>>();

  const goToPatientUpdate = () =>
    navigate(Navigators.PATIENT_UPDATE_STACK_NAVIGATOR, {
      // @ts-expect-error nesting is wonky
      screen: Screens.PATIENT_PROFILE_UPDATE,
      params: { id: patient.patientId, exitOnBack: true }
    });

  const { exitTour } = useTourGuide();

  return (
    <Container>
      <IconContainer>
        <AnimalIcon color={colors.surface} size={IconSize.XS} species={patient.species} />
      </IconContainer>
      <Name>{patient.name}</Name>
      <GuideElement
        id={STEPS.EDIT_INFO}
        body={t('tour.edit')}
        onContinue={() => {
          exitTour();
          goToPatientUpdate();
        }}
        semiTransparentBg
      >
        {showEditButton && (
          <EditButton
            icon={Edit2}
            onPress={() =>
              navigate(Navigators.PATIENT_UPDATE_STACK_NAVIGATOR, {
                // @ts-expect-error nesting is wonky
                screen: Screens.PATIENT_PROFILE_UPDATE,
                params: { id: patient.patientId, exitOnBack: true }
              })
            }
          />
        )}
      </GuideElement>
    </Container>
  );
};

export default Header;

const Container = styled.View`
  flex-direction: row;
  justify-content: flex-start;
  padding-horizontal: ${Margin.ExtraLarge}px;
  align-items: center;
  gap: ${Margin.Medium}px;
  ${!IS_WEB && `margin-top: ${-Size.S}` /** compensation for masked view */}
`;

const Name = styled(Body)`
  font-weight: ${Weight.BOLD};
  color: ${({ theme }) => theme.colors.text};
  flex-grow: 1;
`;

const EditButton = styled(HoverButton).attrs(({ theme }) => ({
  buttonColor: theme.colors.text,
  flexDirection: 'row-reverse'
}))`
  padding: ${Margin.Small}px;
  flex-shrink: 0;
`;

const IconContainer = styled.View`
  width: ${IconSize.M}px;
  height: ${IconSize.M}px;
  border-radius: ${IconSize.M}px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.onSurface};
  flex-shrink: 0;
`;
